import { useEffect, useState } from "react";
import type { CallLog } from "@cometchat/calls-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import * as calls from "./callSdk";
import { Selection } from "./useChatData";

/** getInitiatedAt()'s unit isn't documented by the calls SDK — CometChat's
 * chat-message timestamps are epoch seconds, but this REST-backed call log
 * may hand back milliseconds instead. Guard against both. */
function formatCallTime(ts?: number): string {
  if (!ts) return "";
  const d = new Date(ts > 1e12 ? ts : ts * 1000);
  return d.toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const STATUS_LABEL: Record<string, string> = {
  ongoing: "Ongoing",
  busy: "Busy",
  rejected: "Declined",
  cancelled: "Cancelled",
  ended: "Ended",
  missed: "Missed",
};

/** The "Calls" rail tab — call history (who, when, how long, missed or
 * answered), sourced from CometChat's call log API. */
export default function CallsPanel({
  onClose,
  onOpen,
}: {
  onClose: () => void;
  onOpen: (s: Selection) => void;
}) {
  const [logs, setLogs] = useState<CallLog[] | null>(null);
  const [error, setError] = useState(false);
  const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();

  useEffect(() => {
    let cancelled = false;
    calls
      .fetchCallLogs(40)
      .then((list) => {
        if (!cancelled) setLogs(list);
      })
      .catch((e) => {
        console.warn("call log fetch failed", e);
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const open = (log: CallLog) => {
    const receiverType = log.getReceiverType?.();
    if (receiverType === "group") {
      const g = log.getReceiver() as any;
      onOpen({ kind: "group", id: g?.getGuid?.() ?? "", name: g?.getName?.() ?? "Channel", subtitle: "Channel" });
    } else {
      const initiator = log.getInitiator() as any;
      const other = initiator?.getUid?.() === meUid ? (log.getReceiver() as any) : initiator;
      if (!other?.getUid) return;
      onOpen({ kind: "user", id: other.getUid(), name: other.getName?.() || other.getUid(), subtitle: "Direct message" });
    }
    onClose();
  };

  return (
    <aside className="details-panel later-panel">
      <div className="modal-head">
        <b>Calls</b>
        <button className="hbtn" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <div className="modal-section">
        <div className="modal-list">
          {logs === null && !error && <div className="sect-empty">Loading…</div>}
          {error && <div className="sect-empty">Couldn't load call history</div>}
          {logs && logs.length === 0 && <div className="sect-empty">No calls yet</div>}
          {logs?.map((log) => {
            const initiator = log.getInitiator() as any;
            const isOutgoing = initiator?.getUid?.() === meUid;
            const receiverType = log.getReceiverType?.();
            const other =
              receiverType === "group"
                ? (log.getReceiver() as any)
                : isOutgoing
                ? (log.getReceiver() as any)
                : initiator;
            const name = other?.getName?.() || other?.getGuid?.() || other?.getUid?.() || "Unknown";
            const status = STATUS_LABEL[log.getStatus?.() || ""] || log.getStatus?.() || "";
            const mins = log.getTotalDurationInMinutes?.() || 0;
            const isVideo = log.getType?.() === "video";
            return (
              <div className="later-item" key={log.getSessionID?.() || `${name}-${log.getInitiatedAt?.()}`}>
                <button className="later-item__main" onClick={() => open(log)}>
                  <span className="later-item__text">
                    {isOutgoing ? "↗" : "↙"} {isVideo ? "Video" : "Audio"} · {name}
                  </span>
                  <span className="later-item__meta">
                    {status}
                    {mins > 0 ? ` · ${mins}m` : ""} · {formatCallTime(log.getInitiatedAt?.())}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
